import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScoreBadge, { getScoreConfig } from "@/components/ScoreBadge";
import LoadingSteps from "@/components/LoadingSteps";
import UrlForm from "@/components/UrlForm";
import Seo from "@/components/Seo";
import { breadcrumbJsonLd } from "@/lib/seo-data";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  ExternalLink,
  RotateCcw,
  Share2,
  Calendar,
  Clock,
  ShieldAlert,
  HelpCircle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const STALE_THRESHOLD_HOURS = 24;

// Score convention:
// 0 = très suspect / risque élevé
// 100 = très rassurant / risque faible

function formatDate(iso) {
  if (!iso) return "";

  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shouldRefreshLegacyAnalysis(data) {
  if (!data) return true;

  const hasTimeoutSignal = (data.signals || []).some(
    (s) => s.key === "analysis_timeout"
  );

  if (
    hasTimeoutSignal ||
    data.fetchStatus === "timeout" ||
    String(data.summary || "").toLowerCase().includes("interrompue") ||
    String(data.summary || "").toLowerCase().includes("délai maximal")
  ) {
    return true;
  }

  if (!data.fetchStatus || !data.analysisConfidence) return true;
  if (!Array.isArray(data.blockedSignals) || !Array.isArray(data.unverifiableChecks)) {
    return true;
  }

  const scannedAt = data.scanned_at || data.scannedAt;

  if (scannedAt) {
    const hoursSinceScan =
      (Date.now() - new Date(scannedAt).getTime()) / (1000 * 60 * 60);

    if (hoursSinceScan > STALE_THRESHOLD_HOURS) {
      return true;
    }
  }

  const detected = new Set(
    (data.signals || []).filter((s) => s.detected).map((s) => s.key)
  );

  const hasLegacyAbsencePattern =
    detected.has("no_legal_mentions") &&
    detected.has("no_cgv") &&
    detected.has("no_privacy_policy") &&
    detected.has("no_contact_details") &&
    detected.has("no_company_info") &&
    !detected.has("suspicious_promos") &&
    !detected.has("suspicious_language");

  return (
    hasLegacyAbsencePattern &&
    (data.risk_level === "vigilance" || data.risk_level === "risque_eleve")
  );
}

function RecoIcon({ type }) {
  if (type === "danger") {
    return <ShieldAlert className="w-4 h-4 text-[#B42318] flex-shrink-0" />;
  }

  if (type === "warning") {
    return <AlertTriangle className="w-4 h-4 text-[#B7791F] flex-shrink-0" />;
  }

  return <Info className="w-4 h-4 text-[#163A5F] flex-shrink-0" />;
}

function RecoCard({ rec }) {
  const styles = {
    danger: "bg-[#FEECEC] border-[#F9BBBB] text-[#B42318]",
    warning: "bg-[#FFF4E5] border-[#F6D58C] text-[#B7791F]",
    info: "bg-[#EEF4F8] border-[#CBD5E1] text-[#163A5F]",
  };

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border ${
        styles[rec.type] || styles.info
      }`}
    >
      <RecoIcon type={rec.type} />
      <span className="text-sm leading-relaxed">{rec.text}</span>
    </div>
  );
}

function SignalCard({ signal, variant }) {
  const isWarning = variant === "warning";
  const isUnverifiable = variant === "unverifiable";

  let containerClass;
  let titleColor;
  let msgColor;
  let iconEl;

  if (isUnverifiable) {
    containerClass = "bg-[#F8FAFC] border-[#CBD5E1]";
    titleColor = "text-[#4B5563]";
    msgColor = "text-[#6B7280]";
    iconEl = <HelpCircle className="w-4 h-4 flex-shrink-0 text-[#94A3B8]" />;
  } else if (isWarning) {
    containerClass =
      signal.severity === "high"
        ? "bg-[#FEECEC] border-[#F9BBBB]"
        : "bg-[#FFF4E5] border-[#F6D58C]";

    titleColor =
      signal.severity === "high" ? "text-[#B42318]" : "text-[#B7791F]";

    msgColor =
      signal.severity === "high" ? "text-[#8B1A10]" : "text-[#92600A]";

    iconEl = (
      <AlertTriangle
        className={`w-4 h-4 flex-shrink-0 ${
          signal.severity === "high" ? "text-[#B42318]" : "text-[#B7791F]"
        }`}
      />
    );
  } else {
    containerClass = "bg-[#E7F3EF] border-[#A7D4C7]";
    titleColor = "text-[#2F6B5F]";
    msgColor = "text-[#285B51]";
    iconEl = <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-[#2F6B5F]" />;
  }

  return (
    <div
      className={`p-4 rounded-lg border ${containerClass}`}
      data-testid={`signal-card-${signal.key}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{iconEl}</div>

        <div>
          <p className={`text-sm font-semibold mb-1 ${titleColor}`}>
            {signal.label_fr}

            {signal.severity === "high" && isWarning && (
              <span className="ml-2 text-xs font-bold bg-[#F9BBBB] text-[#B42318] px-1.5 py-0.5 rounded">
                Important
              </span>
            )}

            {isUnverifiable && (
              <span className="ml-2 text-xs font-medium bg-[#E5E7EB] text-[#6B7280] px-1.5 py-0.5 rounded">
                {signal.confidence === "unverifiable" ? "Non vérifiable" : "Non confirmé"}
              </span>
            )}
          </p>

          <p className={`text-xs leading-relaxed ${msgColor}`}>
            {signal.message}
          </p>

          <span className="text-xs text-[#94A3B8] mt-1 inline-block">
            {signal.category}
          </span>
        </div>
      </div>
    </div>
  );
}

function shouldHideSignalFromDisplay(signal, allSignals) {
  if (!signal) return false;

  // Avoid showing two positive domain-age cards for old domains.
  // Keep "domain_too_recent" as the main display signal when both are positive.
  if (
    signal.key === "domain_young" &&
    signal.detected === false &&
    signal.confidence !== "unverifiable" &&
    signal.confidence !== "uncertain"
  ) {
    const recentSignal = (allSignals || []).find(
      (s) => s.key === "domain_too_recent"
    );

    if (
      recentSignal &&
      recentSignal.detected === false &&
      recentSignal.confidence !== "unverifiable" &&
      recentSignal.confidence !== "uncertain"
    ) {
      return true;
    }
  }

  return false;
}

function isNeutralOrUnconfirmedSignal(signal) {
  if (!signal) return false;

  const confidence = signal.confidence;

  if (confidence === "unverifiable" || confidence === "uncertain") {
    return true;
  }

  // External reputation is only reassuring when the backend has a real
  // positive or mixed-positive reputation tier. Otherwise, "not verified"
  // or ambiguous reputation should stay neutral.
  if (
    signal.key === "external_reputation_warning" &&
    signal.detected === false &&
    signal.reputation_tier !== "positive" &&
    signal.reputation_tier !== "mixed_positive"
  ) {
    return true;
  }

  return false;
}

function classifySignals(signals) {
  const warnings = [];
  const unverifiable = [];
  const positive = [];

  for (const signal of signals || []) {
    if (shouldHideSignalFromDisplay(signal, signals)) {
      continue;
    }

    if (isNeutralOrUnconfirmedSignal(signal)) {
      unverifiable.push(signal);
    } else if (signal.detected) {
      warnings.push(signal);
    } else {
      positive.push(signal);
    }
  }

  return { warnings, unverifiable, positive };
}

function shouldShowAlreadyOrderedBox(analysis, warnings, unverifiable) {
  if (!analysis) return false;

  const score = Number(analysis.score);
  const fetchStatus = analysis.fetchStatus;

  return (
    score < 70 ||
    warnings.length > 0 ||
    unverifiable.length >= 3 ||
    fetchStatus === "blocked" ||
    fetchStatus === "failed"
  );
}

function getPartialMessage(fetchStatus) {
  if (fetchStatus === "blocked") {
    return "Ces éléments n'ont pas pu être vérifiés automatiquement en raison des mécanismes de protection du site.";
  }

  if (fetchStatus === "failed") {
    return "Ces éléments n'ont pas pu être vérifiés automatiquement car le site n'était pas accessible au moment de l'analyse.";
  }

  return "Ces éléments n'ont pas pu être vérifiés automatiquement, probablement en raison d'un rendu dynamique (JavaScript).";
}

export default function AnalysisPage() {
  const { domain } = useParams();
  const location = useLocation();

  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const stateUrl = location.state?.url;

    setLoading(true);
    setError(null);

    if (stateUrl) {
      axios
        .post(`${API}/analyser`, { url: stateUrl })
        .then((response) => {
          setAnalysis(response.data);
          setLoading(false);
        })
        .catch((err) => {
          setError(
            err.response?.data?.detail ||
              "Erreur lors de l'analyse. Veuillez réessayer."
          );
          setLoading(false);
        });

      return;
    }

    axios
      .get(`${API}/analyses/domaine/${domain}`)
      .then(async (response) => {
        const cached = response.data;

        if (!shouldRefreshLegacyAnalysis(cached)) {
          setAnalysis(cached);
          setLoading(false);
          return;
        }

        try {
          const fresh = await axios.post(`${API}/analyser`, { url: domain });
          setAnalysis(fresh.data);
        } catch {
          setAnalysis(cached);
        } finally {
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  }, [domain, location.state]);

  const handleShare = () => {
    navigator.clipboard
      ?.writeText(window.location.origin + `/analyse/${domain}`)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  const cfg = analysis ? getScoreConfig(analysis.score, analysis.risk_level) : null;

  const { warnings, unverifiable, positive } = analysis
    ? classifySignals(analysis.signals)
    : { warnings: [], unverifiable: [], positive: [] };

  const showAlreadyOrderedBox = shouldShowAlreadyOrderedBox(
    analysis,
    warnings,
    unverifiable
  );

  const seoTitle = analysis
    ? `Analyse de ${analysis.domain} — Score ${analysis.score}/100`
    : `Analyse de ${domain}`;

  const seoDescription = analysis
    ? `${analysis.risk_label} (${analysis.score}/100) pour ${analysis.domain}. ${
        analysis.summary || ""
      }`.slice(0, 280)
    : `Résultat d'analyse de fiabilité pour ${domain} sur Fiable ou Pas.`;

  return (
    <div className="min-h-screen flex flex-col page-enter">
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={`/analyse/${domain}`}
        noindex
        jsonLd={breadcrumbJsonLd([
          { name: "Accueil", path: "/" },
          { name: "Analyse", path: `/analyse/${domain}` },
        ])}
      />

      <Header />

      <main className="flex-1 bg-[#F8FAFC] py-10">
        <div className="container-app max-w-3xl">
          {loading && (
            <div className="card-base p-8" data-testid="analysis-loading">
              <LoadingSteps domain={domain} />
            </div>
          )}

          {!loading && error && (
            <div className="card-base p-8 text-center" data-testid="analysis-error">
              <AlertTriangle className="w-12 h-12 text-[#B7791F] mx-auto mb-4" />

              <h2 className="text-xl font-bold text-[#111827] mb-2">
                Analyse impossible
              </h2>

              <p className="text-[#4B5563] mb-6">{error}</p>

              <Link to="/" className="btn-primary inline-flex items-center gap-2">
                <RotateCcw className="w-4 h-4" />
                Réessayer
              </Link>
            </div>
          )}

          {!loading && !error && !analysis && (
            <div
              className="card-base p-8 text-center"
              data-testid="analysis-not-found"
            >
              <Info className="w-12 h-12 text-[#163A5F] mx-auto mb-4 opacity-50" />

              <h2 className="text-xl font-bold text-[#111827] mb-2">
                Aucune analyse trouvée pour{" "}
                <span className="font-mono">{domain}</span>
              </h2>

              <p className="text-[#4B5563] mb-6">
                Ce site n&apos;a pas encore été analysé. Lancez une nouvelle
                analyse.
              </p>

              <div className="max-w-md mx-auto">
                <UrlForm />
              </div>
            </div>
          )}

          {!loading && !error && analysis && cfg && (
            <div className="space-y-5" data-testid="analysis-result">
              <div
                className="card-base p-6 sm:p-8"
                style={{ borderTop: `4px solid ${cfg.ring}` }}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-[#6B7280] mb-1">
                      Site analysé
                    </p>

                    <h1 className="text-2xl font-extrabold font-heading text-[#111827] break-all">
                      {analysis.domain}
                    </h1>

                    <div className="flex items-center gap-3 mt-2 text-xs text-[#6B7280] flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(analysis.scanned_at)}
                      </span>

                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {analysis.scan_duration_ms}ms
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={handleShare}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#4B5563] hover:bg-[#F1F5F9] transition-colors flex-shrink-0"
                    data-testid="share-button"
                  >
                    <Share2 className="w-4 h-4" />
                    {copied ? "Lien copié !" : "Partager"}
                  </button>
                </div>

                <div className="flex flex-col items-center py-4">
                  <ScoreBadge
                    score={analysis.score}
                    riskLevel={analysis.risk_level}
                    size="lg"
                  />
                  <p className="text-xs text-[#6B7280] text-center max-w-md mx-auto mt-3 leading-relaxed">
                    Ce score est un indicateur automatisé, pas une garantie. Vérifiez toujours les informations importantes avant d’acheter.
                  </p>
                </div>
              </div>

              <div className="card-base p-6" data-testid="analysis-summary">
                <h2 className="font-bold text-[#111827] mb-3 font-heading">
                  Résumé de l&apos;analyse
                </h2>

                <p className="text-[#4B5563] text-sm leading-relaxed">
                  {analysis.summary}
                </p>
              </div>

              {analysis.fetchStatus === "blocked" && (
                <div
                  className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl p-5"
                  data-testid="blocked-analysis-warning"
                >
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="w-5 h-5 text-[#4B5563] flex-shrink-0 mt-0.5" />

                    <div>
                      <p className="font-semibold text-[#111827] mb-1">
                        Analyse partielle — le site limite l&apos;analyse
                        automatisée
                      </p>

                      <p className="text-sm text-[#4B5563]">
                        Certaines vérifications n&apos;ont pas pu être réalisées
                        car le site semble utiliser des mécanismes limitant
                        l&apos;inspection automatisée.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {(analysis.fetchStatus === "js_required" ||
                analysis.fetchStatus === "partial") && (
                <div
                  className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl p-5"
                  data-testid="partial-analysis-warning"
                >
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-[#4B5563] flex-shrink-0 mt-0.5" />

                    <div>
                      <p className="font-semibold text-[#111827] mb-1">
                        Analyse partielle — contenu du site difficile à vérifier
                        automatiquement
                      </p>

                      <p className="text-sm text-[#4B5563]">
                        Le site semble fortement dépendre du JavaScript ou
                        d&apos;un rendu dynamique. Certaines vérifications
                        n&apos;ont pas pu être confirmées automatiquement.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {analysis.fetchStatus === "failed" && (
                <div
                  className="bg-[#FFF4E5] border border-[#F6D58C] rounded-xl p-5"
                  data-testid="unreachable-warning"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-[#B7791F] flex-shrink-0 mt-0.5" />

                    <div>
                      <p className="font-semibold text-[#B7791F] mb-1">
                        Site inaccessible
                      </p>

                      <p className="text-sm text-[#92600A]">
                        Nous n&apos;avons pas pu récupérer le site pour effectuer
                        l&apos;analyse.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {warnings.length > 0 && (
                <div className="card-base p-6" data-testid="warning-signals">
                  <h2 className="font-bold text-[#111827] mb-4 font-heading flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-[#B7791F]" />
                    Points de vigilance détectés ({warnings.length})
                  </h2>

                  <div className="space-y-3">
                    {warnings.map((signal) => (
                      <SignalCard
                        key={signal.key}
                        signal={signal}
                        variant="warning"
                      />
                    ))}
                  </div>
                </div>
              )}

              {unverifiable.length > 0 && (
                <div
                  className="card-base p-6"
                  data-testid="unverifiable-signals"
                >
                  <h2 className="font-bold text-[#111827] mb-4 font-heading flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-[#94A3B8]" />
                    Vérifications non confirmées ou incertaines ({unverifiable.length})
                  </h2>

                  <p className="text-xs text-[#6B7280] mb-3">
                    {getPartialMessage(analysis.fetchStatus)} Ces éléments ne doivent pas être
                    interprétés comme des signaux positifs ou négatifs sans vérification manuelle.
                  </p>

                  <div className="space-y-3">
                    {unverifiable.map((signal) => (
                      <SignalCard
                        key={signal.key}
                        signal={signal}
                        variant="unverifiable"
                      />
                    ))}
                  </div>
                </div>
              )}

              {positive.length > 0 && (
                <div className="card-base p-6" data-testid="positive-signals">
                  <h2 className="font-bold text-[#111827] mb-4 font-heading flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#2F6B5F]" />
                    Éléments rassurants ({positive.length})
                  </h2>

                  <div className="space-y-3">
                    {positive.map((signal) => (
                      <SignalCard
                        key={signal.key}
                        signal={signal}
                        variant="positive"
                      />
                    ))}
                  </div>
                </div>
              )}

              {analysis.recommendations?.length > 0 && (
                <div className="card-base p-6" data-testid="recommendations">
                  <h2 className="font-bold text-[#111827] mb-4 font-heading">
                    Notre recommandation
                  </h2>

                  <div className="space-y-3">
                    {analysis.recommendations.map((rec, index) => (
                      <RecoCard key={index} rec={rec} />
                    ))}
                  </div>
                </div>
              )}

              {showAlreadyOrderedBox && (
                <div
                  className="card-base p-6 bg-[#F8FAFC]"
                  data-testid="already-ordered"
                >
                  <h2 className="font-bold text-[#111827] mb-3 font-heading">
                    Vous avez déjà commandé ?
                  </h2>

                  <p className="text-sm text-[#4B5563] mb-3 leading-relaxed">
                    Si vous avez déjà payé sur ce site et que plusieurs signaux
                    vous inquiètent, agissez rapidement et conservez toutes les
                    preuves.
                  </p>

                  <ul className="space-y-2 text-sm text-[#4B5563]">
                    {[
                      "Contactez votre banque le plus rapidement possible si vous pensez avoir été victime d'une fraude ou si la commande n'arrive pas.",
                      "Conservez toutes les preuves : emails, captures d'écran, numéro de commande, URL du site et relevé bancaire.",
                      "Signalez le site sur SignalConso si vous pensez être face à une pratique commerciale abusive.",
                      "En cas de préjudice financier, rapprochez-vous des autorités compétentes ou d'une association de consommateurs.",
                    ].map((text) => (
                      <li key={text} className="flex items-start gap-2">
                        <span className="text-[#CBD5E1] flex-shrink-0">•</span>
                        {text}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="card-base p-6" data-testid="official-resources">
                <h2 className="font-bold text-[#111827] mb-4 font-heading">
                  Ressources officielles en France
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    {
                      href: "https://signal.conso.gouv.fr",
                      title: "SignalConso",
                      desc: "Signaler une fraude ou une pratique commerciale abusive.",
                    },
                    {
                      href: "https://www.service-public.fr/particuliers/vosdroits/F35949",
                      title: "Service-Public.fr",
                      desc: "Vos droits en cas de fraude liée à un achat en ligne.",
                    },
                    {
                      href: "https://www.cybermalveillance.gouv.fr",
                      title: "Cybermalveillance.gouv.fr",
                      desc: "Assistance et prévention des actes malveillants numériques.",
                    },
                    {
                      href: "https://www.cnil.fr",
                      title: "CNIL",
                      desc: "Pour signaler des pratiques douteuses liées à vos données.",
                    },
                  ].map(({ href, title, desc }) => (
                    <a
                      key={href}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 p-4 rounded-lg bg-[#EEF4F8] border border-[#E5E7EB] hover:bg-[#E2EDF4] transition-colors"
                    >
                      <ExternalLink className="w-4 h-4 text-[#163A5F] flex-shrink-0 mt-0.5" />

                      <div>
                        <p className="text-sm font-semibold text-[#163A5F]">
                          {title}
                        </p>

                        <p className="text-xs text-[#4B5563] mt-0.5">
                          {desc}
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              <div
                className="text-center text-xs text-[#94A3B8] pb-4 max-w-2xl mx-auto leading-relaxed"
                data-testid="analysis-disclaimer"
              >
                Cette analyse est fournie à titre informatif uniquement. Les
                résultats sont basés sur une analyse automatisée et peuvent être
                incomplets. Ils ne constituent pas un avis juridique, une
                décision officielle ni une garantie. Fiable ou Pas n&apos;est pas
                une autorité légale ou officielle.
              </div>

              <div className="card-base p-6 text-center">
                <p className="text-sm text-[#4B5563] mb-4">
                  Analyser un autre site ?
                </p>

                <UrlForm />
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}